import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { PERCENT, SLIDER_COMMIT_DELAY_MS } from '../../../lib/device/catalog';

export interface BarGestureHandlers {
  readonly onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
  readonly onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void;
  readonly onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void;
  readonly onPointerCancel: (event: ReactPointerEvent<HTMLDivElement>) => void;
}

export interface BarGestureOptions {
  /** Percentages are only produced while this is true (online, and switched on where it applies). */
  readonly enabled: boolean;
  readonly onCommit: (percentage: number) => void;
  /** Coalescing window for intermediate drag values. Defaults to the shared slider delay. */
  readonly commitDelayMs?: number;
}

export interface BarGesture {
  /**
   * Percentage while a pointer is engaged, otherwise null. Held in state
   * rather than a ref so the drag is visible to the current render.
   */
  readonly draggingValue: number | null;
  readonly isDragging: boolean;
  /** Spread onto the draggable element. */
  readonly handlers: BarGestureHandlers;
}

const clampPercent = (value: number): number =>
  Math.min(PERCENT.MAX, Math.max(PERCENT.MIN, Math.round(value)));

const percentFromPointer = (event: ReactPointerEvent<HTMLDivElement>): number => {
  const rect = event.currentTarget.getBoundingClientRect();
  if (rect.width === PERCENT.MIN) return PERCENT.MIN;
  return clampPercent(((event.clientX - rect.left) / rect.width) * PERCENT.MAX);
};

/**
 * Pointer-driven 0–100 bar shared by the dimmer and curtain sliders.
 *
 * Uses pointer events with capture, so the same code drives a finger, a stylus
 * and a mouse, and a drag keeps tracking after the pointer leaves the bar.
 */
export function useBarGesture({
  enabled,
  onCommit,
  commitDelayMs = SLIDER_COMMIT_DELAY_MS,
}: BarGestureOptions): BarGesture {
  const [draggingValue, setDraggingValue] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Read only from callbacks — never during render. */
  const pendingRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Drop any pending commit if the control unmounts mid-drag.
  useEffect(() => clearTimer, [clearTimer]);

  const scheduleCommit = useCallback(
    (percentage: number) => {
      pendingRef.current = percentage;
      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (pendingRef.current !== null) onCommit(pendingRef.current);
      }, commitDelayMs);
    },
    [clearTimer, commitDelayMs, onCommit],
  );

  const commitNow = useCallback(() => {
    clearTimer();
    const percentage = pendingRef.current;
    pendingRef.current = null;
    setDraggingValue(null);
    if (percentage !== null) onCommit(percentage);
  }, [clearTimer, onCommit]);

  const handlers: BarGestureHandlers = {
    onPointerDown: (event) => {
      if (!enabled) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      const percentage = percentFromPointer(event);
      setDraggingValue(percentage);
      scheduleCommit(percentage);
    },
    onPointerMove: (event) => {
      if (!enabled || draggingValue === null) return;
      const percentage = percentFromPointer(event);
      setDraggingValue(percentage);
      scheduleCommit(percentage);
    },
    onPointerUp: () => {
      if (draggingValue === null) return;
      commitNow();
    },
    onPointerCancel: () => {
      if (draggingValue === null) return;
      commitNow();
    },
  };

  return { draggingValue, isDragging: draggingValue !== null, handlers };
}
